/*************************************************************************
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getCursorInfoObjectFromTransformedQuad } from "dc-preview-utils/src/Utils/CursorUtils";
import { getCursorQuad, getIsCursorVisible } from "dc-preview-utils/src/selectors/cursorSelectors";
import * as codes from "dc-preview-utils/src/Constants/key-codes";
import { transformPDFToView, transformQuadFromPDFToView } from "dc-preview-utils/src/Utils/PageUtils";
import { SELECTION_PRIORITY } from "dc-preview-utils/src/Constants/constants";
import { getPageBeforeOrAtOffset } from "selectors/pageNumber";
import { getPagesViewXPos, getPagesViewYPos, getTotalHeightTillPageTop } from "selectors/pagedViewState";
import docApi from "api";
import { getPagesInView, getTotalPages, isCoverPageMode, isTwoColumnViewMode } from "selectors/documentSelectors";
import { getPageHeight, getPageWidth, getPageOrigHeight, isLeftPageIndex } from "selectors/pageSelectors";
import { pageContentHandler } from "pageContentHandler";
import { toolsSupportingCaretMode } from "constants/enums";
import { getActiveVerb } from "selectors/quickToolsSelectors";
import { isEditActive } from "selectors/overlayDropinSelectors";
import { getFocusableDescendants, isLinkNode, isMarkupAnnotNode } from "utils/AccessibilityUtils";
import { isInputOrEditableDiv, isBody } from "utils/DomUtils";
import * as stores from "stores";
import { getTabbablePDFElementsInPage, getIsCaretSetByFocus } from "selectors/accessibilitySelectors";

export const transformQuads = (quad, pageIndex, stores) => {
  const newQuads = transformQuadFromPDFToView(quad, pageIndex, stores);
  const quadPoints = [];
  for (let i = 0; i < 8; i += 2) {
    let obj = {};
    obj.x = newQuads[i];
    obj.y = newQuads[i + 1];
    quadPoints.push(obj);
  }
  return { tl: quadPoints[0], tr: quadPoints[1], br: quadPoints[2], bl: quadPoints[3] };
};

export const getTransformedCursorInfo = (cursor, pageIndex, stores) => {
  const transformedQuad = transformQuads(cursor, pageIndex, stores);
  return getCursorInfoObjectFromTransformedQuad(transformedQuad, getIsCursorVisible(stores));
};

export const resetCaretMode = () => {
  pageContentHandler.getTextManagerInstance().clearCaret();
};

export const callTextSelection = async (endPoint, stores, jumpToCaret = false) => {
  const res = await docApi.textSelection(endPoint, endPoint, SELECTION_PRIORITY, undefined, true, true);
  const textManagerInstance = pageContentHandler.getTextManagerInstance();
  if (res && res.data && res.data.quads && res.data.quads.length > 0) {
    textManagerInstance._handleCaretReply(res, true);
    if (jumpToCaret) {
      stores.pagedViewState.goToLocation(
        res.data.quads[0].pageNum - 1,
        res.data.quads[0].quads[0][0],
        getPageOrigHeight(stores, res.data.quads[0].pageNum - 1) -
          Math.max(res.data.quads[0].quads[0][1], res.data.quads[0].quads[0][5])
      );
    }
    return true;
  }
  return false;
};

export const isCaretModeEnabledInActiveVerb = stores => toolsSupportingCaretMode.includes(getActiveVerb(stores));

export const shouldHideCaret = element => {
  if (isBody(element)) return false;
  // Hide caret in case current element is keyboard focusable or editable
  const keyboardFocusableNodes = getFocusableDescendants(element);
  if (keyboardFocusableNodes.length === 0) {
    return isInputOrEditableDiv(element);
  }
  return true;
};

export const moveCaretToFirstVisibleCoordinate = async (forceShow = false, shouldJump = true) => {
  if (
    isCaretModeEnabledInActiveVerb(stores) &&
    (forceShow || !shouldHideCaret(document.activeElement)) &&
    !isEditActive(stores)
  ) {
    const { yInPage } = getPageBeforeOrAtOffset(stores),
      startPageIndex = Math.max(0, getPagesInView(stores).start + (yInPage === -1));

    let pageIndex = startPageIndex;
    let y = yInPage !== -1 ? yInPage : 0;
    y = Math.min(y + 15, getPageHeight(stores, pageIndex));
    let numPartialPages = isTwoColumnViewMode(stores) && !(isCoverPageMode(stores) && startPageIndex === 0) ? 2 : 1;

    do {
      const isTwoColRightPage = isTwoColumnViewMode(stores) && !isLeftPageIndex(stores, pageIndex);
      const x = Math.max(getPagesViewXPos(stores) - (isTwoColRightPage ? getPageWidth(stores, pageIndex) : 0), 0);

      const setCaretInView = async (x, y, pageIndex) => {
        const endPoint = { x, y, pageNum: pageIndex + 1 };
        const res = await callTextSelection(endPoint, stores, shouldJump);
        let { x: caretX, y: caretY } = getCaretMidPoint();
        ({ x: caretX, y: caretY } = transformPDFToView(caretX, caretY, pageIndex, stores));
        return res && caretY + getTotalHeightTillPageTop(stores, pageIndex) >= getPagesViewYPos(stores);
      };

      if (await setCaretInView(x, y, pageIndex)) return true;
      if (numPartialPages > 0) {
        if (await setCaretInView(x, getPageHeight(stores, pageIndex), pageIndex)) return true;
        numPartialPages--;
        if (numPartialPages === 0) y = 0;
      }
      pageIndex = (pageIndex + 1) % getTotalPages(stores);
    } while (pageIndex !== startPageIndex);
  }
  return false;
};

export const handleKeyboardNavigation = event => {
  switch (event.keyCode) {
    case codes.PAGE_UP:
    case codes.PAGE_DOWN:
      setTimeout(() => moveCaretToFirstVisibleCoordinate(false, false), 500);
      break;
    default:
      return false;
  }
  return false;
};

export const moveCaretToDocEnd = async stores => {
  for (let pageIndex = getTotalPages(stores) - 1; pageIndex >= 0; pageIndex--) {
    const endPoint = {
      x: getPageWidth(stores, pageIndex),
      y: getPageHeight(stores, pageIndex),
      pageNum: pageIndex + 1
    };
    // eslint-disable-next-line no-await-in-loop
    const res = await callTextSelection(endPoint, stores, true);
    if (res) return true;
  }
  return false;
};

export const moveCaretToDocBegin = async stores => {
  for (let pageIndex = 0; pageIndex < getTotalPages(stores); pageIndex++) {
    const endPoint = {
      x: 0,
      y: 0,
      pageNum: pageIndex + 1
    };
    // eslint-disable-next-line no-await-in-loop
    const res = await callTextSelection(endPoint, stores, true);
    if (res) return true;
  }
  return false;
};

export const setCaretAtStartOfRect = async (rect, pageIndex) => {
  if (isCaretInRect(rect, pageIndex)) return;

  const { xMin, yMin, yMax } = rect;
  const point = { x: xMin, y: getPageOrigHeight(stores, pageIndex) - (yMin + yMax) / 2, pageNum: pageIndex + 1 };

  const res = await docApi.textSelectionFromPDFCoordinates(point, point, SELECTION_PRIORITY, undefined, true, true);
  pageContentHandler.getTextManagerInstance()._handleCaretReply(res, true);
  stores.accessibilityStore.setIsCaretSetByFocus(true);
};

const isPointInRect = ({ x, y }, { xMin, xMax, yMin, yMax }) =>
  xMin - 1 <= x && x <= xMax + 1 && yMin - 1 <= y && y <= yMax + 1;

const getCaretMidPoint = () => {
  const cursorQuads = getCursorQuad(stores);
  if (cursorQuads.length === 0) return {};
  const [x1, y1, , , , , x2, y2] = cursorQuads;
  const x = (x1 + x2) / 2,
    y = (y1 + y2) / 2;
  return { x, y };
};

export const isCaretInRect = (rect, pageIndex) => {
  const point = getCaretMidPoint();
  point.y = getPageOrigHeight(stores, pageIndex) - point.y;
  return isPointInRect(point, rect);
};

export const focusTabbableElementAtCaret = pageIndex => {
  if (getIsCaretSetByFocus(stores)) {
    stores.accessibilityStore.setIsCaretSetByFocus(false);
    return;
  }
  const tabbableElements = getTabbablePDFElementsInPage(stores, pageIndex) || [];
  const caretMidPoint = getCaretMidPoint();
  caretMidPoint.y = getPageOrigHeight(stores, pageIndex) - caretMidPoint.y;
  const overlappedElements = tabbableElements.filter(({ contentRect }) => isPointInRect(caretMidPoint, contentRect));
  const item = overlappedElements.find(isLinkNode) || overlappedElements.find(isMarkupAnnotNode);
  stores.accessibilityStore.setElementToFocus(item ? { pageIndex, id: item.id } : null);
};
